import { useState, useEffect } from 'react';
import {
  CheckListResult,
  DndGuestList,
  DndReservationList,
  DndRoomList,
  DndTariffList,
  IDeclareNewDayBody,
  IRoomStatusBody,
  IRoomTariffBody,
  IRoomUnblocking,
  IRoomUnblockingDetails,
  NighAuditList,
  Rooms,
} from '../modules/front-office/interface/fo-dnd-interface';
import {
  useLazyGetNightAuditListQuery,
  usePostDeclareNewDayMutation,
  usePostRoomTariffMutation,
  usePostUnblockRoomBlockingMultipleMutation,
  useUpdateRoomStatusMutation,
  useGetNightAuditListQuery,
  useDeleteSessionsMutation,
} from '../api/front-office/front-office.api.rtk';
import useToast from './useToast';
import { useAppSelector } from '../store';
import moment from 'moment';
import { selectFOSalesDate } from '@store/selector/app.selector';

export type DeclareNewDayResponse = {
  responseCode: number;
  responseMessage: string;
  responseResult: boolean;
};

/**
 * Custom hook for managing the Declare New Day process in a hotel management system.
 * This hook handles various operations related to night audit, room status updates,
 * and other end-of-day processes.
 *
 * @returns {Object} An object containing various state variables and functions for the Declare New Day process.
 */
const useDeclareNewDay = () => {
  const [dndCheckList, setDndCheckList] = useState<CheckListResult[]>([
    { count: 0, order: 0, state: 'progress', title: '' },
  ] as CheckListResult[]);

  // Define a selector to extract the propertyID from the Redux store
  const selectedPropertyID = useAppSelector(
    (state) => state.properties.selectedProperty.value,
  );

  //Get Current Sales Date
  const currentSalesDate = useAppSelector(selectFOSalesDate);

  //List
  const [reservationList, setReservationList] = useState<DndReservationList[]>(
    [] as DndReservationList[],
  );
  const [checkOutList, setCheckoutList] = useState<DndGuestList[]>(
    [] as DndGuestList[],
  );
  const [checkInList, setCheckinList] = useState<DndGuestList[]>(
    [] as DndGuestList[],
  );
  const [tariffList, setTariffList] = useState<DndTariffList[]>(
    [] as DndTariffList[],
  );
  const [blockList, setBlockList] = useState<DndRoomList[]>(
    [] as DndRoomList[],
  );
  const [vcdList, setVcdList] = useState<DndRoomList[]>([] as DndRoomList[]);
  const [fipList, setFipList] = useState<DndRoomList[]>([] as DndRoomList[]);

  const [totalTask, setTotalTask] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [getNightAuditList] = useLazyGetNightAuditListQuery();
  const [updateRoomStatus] = useUpdateRoomStatusMutation();
  const [postDeclareNewDay] = usePostDeclareNewDayMutation();
  const [postRoomTariff] = usePostRoomTariffMutation();
  const [deleteSessions] = useDeleteSessionsMutation();
  const [postUnblockRoomBlockingMultiple] =
    usePostUnblockRoomBlockingMultipleMutation();

  const { toast } = useToast();

  const { data: nightAuditListData } = useGetNightAuditListQuery(
    `?filter[type]=0`,
    {
      skip: selectedPropertyID === 'All Properties',
    },
  );

  /**
   * Fetches and sets various lists related to the night audit process.
   * @param {NighAuditList[]} list - The night audit list data.
   */
  const fetchList = (list: NighAuditList[]) => {
    try {
      const l = list || [
        {
          reservation: [],
          checkout: [],
          vcd: [],
          fip: [],
          checkin: [],
          block: [],
          tariff: [],
        },
      ];

      if (!l || l.length === 0) return;

      const [{ reservation, checkout, vcd, fip, checkin, block, tariff }] = l;

      setReservationList(reservation);
      setCheckoutList(checkout);
      setVcdList(vcd);
      setFipList(fip);
      setCheckinList(checkin);
      setBlockList(block);
      setTariffList(tariff);
    } catch (error) {
      console.error('Error fetching list:', error);
    }
  };

  useEffect(() => {
    const checkList = nightAuditListData?.data?.checklist || [];
    const list = nightAuditListData?.data?.list || [];

    const totalTasks = checkList.reduce((sum, task) => sum + task.count, 0);
    setTotalTask(totalTasks);
    setDndCheckList(checkList);
    fetchList(list);
  }, [nightAuditListData]);

  /**
   * Refreshes the night audit list and checklist.
   */
  const refreshList = async () => {
    try {
      setIsLoading(true);
      await getNightAuditList(`?filter[type]=0`)
        .unwrap()
        .then((result) => {
          const checkList = result?.data?.checklist || [];
          const list = result?.data?.list || [];

          const totalTasks = checkList.reduce(
            (sum, task) => sum + task.count,
            0,
          );
          setTotalTask(totalTasks);
          setDndCheckList(checkList);
          fetchList(list);
        })
        .catch();
    } catch (error) {
      console.error('Error fetching checker list:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Updates the status of rooms.
   * @param {Rooms[]} jsonBody - The room status update data.
   */
  const roomStatusUpdate = async (jsonBody: Rooms[]) => {
    const finalJsonBody: IRoomStatusBody = {
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            rooms: jsonBody,
          },
        },
      ],
    };

    // Database Call
    await updateRoomStatus(finalJsonBody)
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: 'Successfully updated room status.',
        });
      })
      .catch((err) =>
        toast({
          severity: 'error',
          message: err,
        }),
      );
  };

  /**
   * Unblocks multiple rooms.
   * @param {IRoomUnblockingDetails[]} jsonBody - The room unblocking details.
   */
  const roomUnblocking = async (jsonBody: IRoomUnblockingDetails[]) => {
    const finalJsonBody: IRoomUnblocking = {
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            blockings: jsonBody,
          },
        },
      ],
    };

    // Database Call
    await postUnblockRoomBlockingMultiple(finalJsonBody)
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: 'Successfully unblocked rooms.',
        });
      })
      .catch((err) =>
        toast({
          severity: 'error',
          message: err,
        }),
      );
  };

  /**
   * Declares a new day in the system.
   * @returns {Promise<boolean>} A promise that resolves to true if the operation was successful, false otherwise.
   */
  const declareNewDay = async (): Promise<DeclareNewDayResponse> => {
    let returnResult: DeclareNewDayResponse = {
      responseCode: 200,
      responseMessage: 'Success',
      responseResult: false,
    };

    const jsonBody: IDeclareNewDayBody = {
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            datum: [
              {
                data: {
                  salesDate: moment(currentSalesDate).format('YYYY-MM-DD'),
                  moduleCode: 'FO',
                  moduleCodeSub: '',
                },
              },
            ],
          },
        },
      ],
    };

    // Database Call
    await postDeclareNewDay(jsonBody)
      .unwrap()
      .then((data) => {
        const responseCode = data?.data?.response.code;
        returnResult = {
          responseCode,
          responseMessage: data?.data?.response.response,
          responseResult: responseCode === 200,
        };
      })
      .catch((error) => {
        toast({
          severity: 'error',
          message: error,
        });
        returnResult = {
          responseCode: 500,
          responseMessage: 'Error in RTK query for Declare New Day.',
          responseResult: false,
        };
      });

    return returnResult;
  };

  /**
   * Posts room tariff information.
   * @returns {Promise<boolean>} A promise that resolves to true if the operation was successful, false otherwise.
   */
  const roomTariffPost = async () => {
    let returnResult = false;

    const jsonBody: IRoomTariffBody = {
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            roomTariffs: [
              {
                roomTariff: {
                  salesDate: currentSalesDate,
                  bookingReferenceId: null,
                },
              },
            ],
          },
        },
      ],
    };

    await postRoomTariff(jsonBody)
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: 'Room Tariff has been posted successfully.',
        });
        returnResult = true;
      })
      .catch(() => (returnResult = false));

    return returnResult;
  };

  const forceLogout = async (propertyID?: string) => {
    await deleteSessions({ payload: `FO`, propertyID: propertyID }).unwrap();
  };

  return {
    dndCheckList,
    totalTask,
    refreshList,
    isLoading,
    reservationList,
    checkOutList,
    checkInList,
    tariffList,
    blockList,
    vcdList,
    fipList,
    fetchList,
    roomStatusUpdate,
    declareNewDay,
    roomTariffPost,
    roomUnblocking,
    forceLogout,
  };
};

export default useDeclareNewDay;
